import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as loginRequest } from '../api/auth.api';
import { useAuth } from '../context/AuthContext';
import { AuthResponse, PendingAuthResponse } from '../api/auth.api';

const isEmail = (value: string) => /.+@.+\..+/.test(value);
const isPhone = (value: string) => /^[0-9+\-()\s]{7,}$/.test(value);

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, setPending } = useAuth();
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [errors, setErrors] = useState<{ identifier?: string; password?: string; general?: string }>({});

  const validateLogin = () => {
    const nextErrors: { identifier?: string; password?: string; general?: string } = {};
    if (!loginIdentifier.trim() || (!isEmail(loginIdentifier) && !isPhone(loginIdentifier))) {
      nextErrors.identifier = 'Enter a valid email or phone number.';
    }
    if (!loginPassword.trim()) {
      nextErrors.password = 'Password is required.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleIdentifierChange = (value: string) => {
    setLoginIdentifier(value);
    if (errors.identifier || errors.general) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.identifier;
        delete newErrors.general;
        return newErrors;
      });
    }
  };

  const handlePasswordChange = (value: string) => {
    setLoginPassword(value);
    if (errors.password || errors.general) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.password;
        delete newErrors.general;
        return newErrors;
      });
    }
  };

  const handleLoginSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateLogin()) {
      return;
    }

    setLoginLoading(true);
    setErrors({}); // Clear previous errors
    try {
      const response = await loginRequest({
        identifier: loginIdentifier,
        password: loginPassword,
      });

      const authData = response.data as AuthResponse | PendingAuthResponse;
      
      if ('status' in authData && authData.status === 'PENDING') {
        setPending();
        navigate('/dashboard');
        return;
      }

      const authResponse = authData as AuthResponse;
      login(authResponse);
      
      // Admin always goes to dashboard (can access admin panels from there)
      // Regular users: check if approved
      if (authResponse.isAdmin) {
        navigate('/dashboard');
      } else if (authResponse.status === 'APPROVED') {
        navigate('/dashboard');
      } else {
        // Not approved - will see blocking screen
        setPending();
        navigate('/dashboard');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message;
      const statusCode = err.response?.status;

      if (statusCode === 401) {
        // Unauthorized - wrong password or invalid credentials
        setErrors({
          general: 'Invalid email/phone or password. Please check your credentials and try again.',
        });
      } else if (statusCode === 404) {
        // User not found
        setErrors({
          general: 'No account found with this email or phone number. Please register first.',
        });
      } else if (statusCode === 400) {
        // Bad request
        setErrors({
          general: errorMessage || 'Invalid input. Please check your email/phone and password.',
        });
      } else if (statusCode === 403) {
        // Forbidden - account might be disabled
        setErrors({
          general: 'Your account has been disabled. Please contact support.',
        });
      } else if (statusCode === 429) {
        // Too many requests
        setErrors({
          general: 'Too many login attempts. Please wait a few minutes and try again.',
        });
      } else if (statusCode >= 500) {
        // Server error
        setErrors({
          general: 'Server error. Please try again later.',
        });
      } else if (err.code === 'ECONNABORTED' || err.message === 'Network Error') {
        // Network error
        setErrors({
          general: 'Network error. Please check your internet connection and try again.',
        });
      } else {
        // Other errors
        setErrors({
          general: errorMessage || 'Login failed. Please try again.',
        });
      }
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-6">
          <div className="card card-surface shadow-sm">
            <div className="card-body p-4">
              <h1 className="h4 mb-3 text-center">TT Cricket Access</h1>
              {errors.general && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {errors.general}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      const newErrors = { ...errors };
                      delete newErrors.general;
                      setErrors(newErrors);
                    }}
                    aria-label="Close"
                  ></button>
                </div>
              )}
              <form onSubmit={handleLoginSubmit} className="d-grid gap-3">
                <div>
                  <label htmlFor="identifier" className="form-label">
                    Identifier <span className="text-danger">*</span>
                  </label>
                  <input
                    id="identifier"
                    type="text"
                    className={`form-control ${errors.identifier ? 'is-invalid' : ''}`}
                    placeholder="Email or Phone Number"
                    value={loginIdentifier}
                    onChange={(event) => handleIdentifierChange(event.target.value)}
                  />
                  {errors.identifier && <div className="invalid-feedback">{errors.identifier}</div>}
                </div>
                <div>
                  <label htmlFor="loginPassword" className="form-label">
                    Password <span className="text-danger">*</span>
                  </label>
                  <input
                    id="loginPassword"
                    type="password"
                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                    value={loginPassword}
                    onChange={(event) => handlePasswordChange(event.target.value)}
                  />
                  {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                </div>
                <button type="submit" className="btn btn-arena" disabled={loginLoading}>
                  {loginLoading ? 'Logging in...' : 'Login'}
                </button>
              </form>
              <div className="text-center mt-3">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none"
                  onClick={() => navigate('/register')}
                >
                  New here? Register as a player
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
